// The session store that replaces `pb.authStore` (the `pocketbase` SDK is gone).
//
// It deliberately keeps the SDK's storage key and JSON shape
// (`pocketbase_auth` = `{"token": ..., "record": ...}`), so a deploy of this
// code signs nobody out and an image rollback signs nobody out either. It also
// keeps the members the app actually used — `token`, `record`, `isValid`,
// `save()`, `clear()`, `onChange()` — with the same semantics; nothing else
// about the SDK's store is reproduced.

export type AuthRecord = {
  id: string;
  [key: string]: unknown;
};

type Listener = (token: string, record: AuthRecord | null) => void;

const STORAGE_KEY = "pocketbase_auth";

// localStorage can be unavailable (private windows, blocked site data) and is
// absent outside the DOM. Losing persistence degrades to a per-tab session;
// throwing here would take the whole app down, so every access is guarded.
const storage = {
  get(): string | null {
    try {
      return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  },
  set(value: string): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, value);
    } catch {
      // ignore
    }
  },
  remove(): void {
    try {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

type Stored = { token: string; record: AuthRecord | null };

function readStored(): Stored {
  try {
    const raw = storage.get();
    if (!raw) return { token: "", record: null };
    const parsed = JSON.parse(raw) as { token?: unknown; record?: unknown; model?: unknown };
    const token = typeof parsed.token === "string" ? parsed.token : "";
    // Pre-v0.23 SDK versions stored the record under `model`; read both so an
    // ancient stored session still signs in.
    const record = (parsed.record ?? parsed.model) as AuthRecord | null | undefined;
    if (record && typeof record === "object" && typeof record.id === "string") {
      return { token, record };
    }
    return { token, record: null };
  } catch {
    // A corrupt entry is indistinguishable from no session; treat it as none.
    return { token: "", record: null };
  }
}

/**
 * Local expiry check of a JWT's `exp` claim, matching the SDK's `isValid`.
 * A malformed token counts as expired — it can never authenticate anything.
 */
export function isTokenExpired(token: string): boolean {
  if (!token) return true;
  const parts = token.split(".");
  if (parts.length !== 3) return true;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: unknown };
    return typeof payload.exp !== "number" || payload.exp <= Date.now() / 1000;
  } catch {
    return true;
  }
}

class AuthStore {
  private stored: Stored = readStored();
  private listeners = new Set<Listener>();

  get token(): string {
    return this.stored.token;
  }

  /** The signed-in user's record as of the last save — a snapshot, not proof. */
  get record(): AuthRecord | null {
    return this.stored.record;
  }

  /**
   * True while a token is present and its `exp` has not passed. Purely local,
   * exactly like the SDK's `isValid`: the server can still have revoked the
   * token (logout rotates the account's token key), and any 401 clears the
   * store. auth-session.ts's expired-session classifier depends on this being
   * an `exp` check and nothing more.
   */
  get isValid(): boolean {
    return !isTokenExpired(this.stored.token);
  }

  /** Records a sign-in (or a refreshed record for the same session). */
  save(token: string, record: AuthRecord | null): void {
    this.stored = { token, record };
    storage.set(JSON.stringify({ token, record }));
    this.emit();
  }

  /** Drops the local session. Server-side revocation is auth-api's logout(). */
  clear(): void {
    if (this.stored.token === "" && this.stored.record === null) return;
    this.stored = { token: "", record: null };
    storage.remove();
    this.emit();
  }

  /** Subscribes to sign-in / sign-out / record refresh. Returns the unsubscriber. */
  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.stored.token, this.stored.record);
  }
}

export const authStore = new AuthStore();
